import { useState } from 'react';
import { useMobile } from '@/lib/query';
import { ts } from '@/lib/format';
import { Badge } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';

export default function AppPreview() {
  const { data } = useMobile();
  const [labStage, setLabStage] = useState<'entry' | 'confirm' | 'done'>('entry');
  const [icmSaved, setIcmSaved] = useState(false);
  return (
    <div>
      <PageBanner />
      <div className="mb-6">
        <h2 className="text-[24px] font-bold text-slate-900">APP 端示意</h2>
        <p className="mt-1 text-[14px] text-slate-500">一线化验 → 写 PLC 二次确认（与 /v1/lab/submit 联调，提交后同驾驶舱联动）</p>
      </div>

      <div className="flex justify-center">
        {/* Phone frame */}
        <div className="w-[360px] rounded-[36px] border-[10px] border-ink-900 bg-canvas p-3 shadow-pop">
          <div className="mb-3 flex items-center justify-between px-1">
            <span className="text-[15px] font-semibold text-slate-700">首页 / 总览</span>
            <span className="pill bg-alarm-bg text-alarm-fg border border-alarm-border">● {data?.alarmCount ?? 0}</span>
          </div>

          <div className="mb-3 text-[12px] text-slate-400">现场快览（演示栅格；完整监控请在 Web 侧栏进入）</div>
          <div className="mb-4 grid grid-cols-2 gap-2">
            {data?.quick.map((q) => (
              <div key={q.label} className="rounded-xl border border-hairline bg-panel p-3 text-center">
                <div className="text-[13px] text-slate-500">{q.label}</div>
                <div className="mt-1 text-[13px] font-semibold tabular-nums text-slate-800">{q.value}</div>
              </div>
            ))}
          </div>

          <div className="mb-2 text-[14px] font-semibold text-slate-700">报警中心（{data?.alarmCount ?? 0}）</div>

          <div className="mb-4 rounded-xl border border-hairline bg-panel p-4">
            <div className="text-[15px] font-semibold text-slate-800">水质化验录入</div>
            <div className="mt-1 text-[12px] text-warn-fg">上次入站 {ts(data?.lab.sampleAt)} · {data?.lab.staleOver24h ? '>24h 已标黄' : '正常'}</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <label className="text-[12px] text-slate-500">氨氮 (mg/L)
                <input readOnly value={data?.lab.nh3n ?? ''} className="mt-1 w-full rounded-lg border border-hairline px-2 py-2 text-sm" />
              </label>
              <label className="text-[12px] text-slate-500">亚盐 (mg/L)
                <input readOnly value={data?.lab.no2 ?? ''} className="mt-1 w-full rounded-lg border border-hairline px-2 py-2 text-sm" />
              </label>
            </div>
            <div className="mt-2 text-[11px] text-slate-400">关注阈值 NH₃-N&gt;1.2 或 NO₂-N&gt;0.6；极危（联动救灾模拟）为 &gt;2 / &gt;1。</div>
            {labStage === 'entry' ? (
              <button onClick={() => setLabStage('confirm')} className="btn btn-dark mt-3 w-full justify-center">下一步：风险确认</button>
            ) : labStage === 'confirm' ? (
              <div className="mt-3 rounded-lg bg-warn-bg/70 p-2 text-[12px] text-warn-fg">
                二次确认（写 PLC）：NH₃-N {data?.lab.nh3n ?? '—'} / NO₂-N {data?.lab.no2 ?? '—'}。确认后将提交并联动驾驶舱。
                <div className="mt-2 flex gap-2">
                  <button onClick={() => setLabStage('done')} className="btn btn-dark flex-1 justify-center">确认提交（示意）</button>
                  <button onClick={() => setLabStage('entry')} className="btn btn-ghost flex-1 justify-center">返回修改</button>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex items-center justify-between rounded-lg bg-ok-bg p-2 text-[12px] text-ok-fg">
                <span>已提交（示意）· 与 /v1/lab/submit 联调</span>
                <button onClick={() => setLabStage('entry')} className="underline">再来一次</button>
              </div>
            )}
          </div>

          <div className="mb-2 text-[14px] font-semibold text-slate-700">ICM 任务（巡检 / 校准 / 维保）</div>
          <div className="rounded-xl border border-hairline bg-panel p-4">
            <div className="text-[12px] text-slate-500">选择任务</div>
            {data?.icmTasks.map((t) => (
              <div key={t.id} className="mt-2 rounded-lg border border-hairline px-3 py-2 text-[13px]">
                <div className="flex items-center justify-between">
                  <span className="text-slate-700">{t.title}</span>
                  <Badge>{t.device}</Badge>
                </div>
                <div className="mt-1 text-[12px] text-slate-400">工单号 {t.workOrder}</div>
              </div>
            ))}
            <button onClick={() => setIcmSaved(true)} disabled={icmSaved} className="btn btn-primary mt-3 w-full justify-center disabled:opacity-60">{icmSaved ? '已保存（示意）✓' : '保存表单'}</button>
            {icmSaved ? <button onClick={() => setIcmSaved(false)} className="mt-1 w-full text-center text-[12px] text-slate-400 hover:underline">重新编辑</button> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
